/* eslint-disable no-console */

/**
 * AI Service Module
 * Handles AI-powered safety analysis and tree generation
 */

const HAZARD_CATEGORY_METADATA = {
  mechanical: {
    label: '1. Mechanische Gefährdungen',
    description: 'A: Mechanische Gefährdungen. B: Bewegte Maschinenteile, gefährliche Oberflächen, Transportmittel, unkontrollierte Teile/Tiere, Gegenstände in Höhe, Druckmedien, Federn, angehobene Last. C: Arbeiten an/in diesen Bereichen.',
    details: {
      A: 'Mechanische Gefährdungen',
      B: [
        'bewegte Maschinenteile mit Quetsch-, Scher-, Stoss-, Schneid-, Stich-, Einzug- oder Fangstellen',
        'Teile mit gefaehrlichen Oberflaechen: Ecken, Spitzen, Kanten, Schneiden, Rauigkeit',
        'bewegte Transport-/Arbeitsmittel',
        'unkontrolliert bewegende Teile',
        'sich unkontrolliert verhaltende Tiere',
        'Gegenstaende in der Hoehe',
        'gestapelte Gegenstaende',
        'nicht standfeste Materialien',
        'unter Druck stehende Medien',
        'vorgespannte Feder',
        'angehobene Last'
      ],
      C: [
        'Im Bereich sich ungeschuetzt bewegter Maschinenteile arbeiten',
        'An Teilen mit gefaehrlichen Oberflaechen arbeiten',
        'Sich im Fahrbereich aufhalten',
        'Bewegte Transport- oder Arbeitsmittel fuehren',
        'Sich im Bereich unkontrolliert bewegender Tiere aufhalten',
        'Den Sturzraum von Gegenstaenden betreten',
        'Sich im Sturzraum nicht standfester Materialien aufhalten',
        'An Behaeltern mit unter Druck stehenden Medien hantieren',
        'Sich im Entlastungsbereich von Druckbehaeltern aufhalten',
        'Sich im Entspannungsbereich von Federn aufhalten',
        'Arbeiten unter anhebbarer Last'
      ]
    }
  },
  falling: {
    label: '2. Sturzgefährdung',
    description: 'A: Sturzgefährdung. B: Arbeitshöhe, Absturzkanten, geneigte Flächen, rutschige Oberflächen, Stolperstellen. C: Aufenthalt/Arbeit in diesen Bereichen.',
    details: {
      A: 'Sturzgefaehrdung',
      B: [
        'Arbeitshoehe (Leitern, Arbeitsbuehnen ...)',
        'Absturzkanten (Podesten, Gelaende, Grube ...)',
        'geneigte Arbeitsflaeche (Rampen, Daechern ...)',
        'rutschige Oberflaeche (Nasse, Schmutz/Ol, Schnee)',
        'Bereiche mit Stolperstellen (Kanten, Loecher, Gegenstaende)'
      ],
      C: [
        'Sich in der Hoehe aufhalten',
        'Auf Arbeitsflaechen arbeiten',
        'Auf geneigter Arbeitsflaeche arbeiten',
        'Sich auf rutschiger Oberflaeche bewegen',
        'Sich in Bereichen mit Stolperstellen bewegen'
      ]
    }
  },
  electrical: {
    label: '3. Elektrische Gefährdung',
    description: 'A: Elektrische Gefährdung. B: Unter Spannung/Hochspannung stehende Teile, Lichtbögen. C: Arbeiten in Nähe dieser Teile.',
    details: {
      A: 'Elektrische Gefaehrdung',
      B: [
        'Unter Spannung stehende Teile',
        'Unter Hochspannung stehende Teile',
        'Apparaturen, die Lichtboegen emittieren'
      ],
      C: [
        'In der Nahe von spannungsfuehrenden Teilen arbeiten',
        'Sich Teilen naehern, die unter Hochspannung stehen',
        'Sich im Wirkbereich von moeglichen Lichtboegen aufhalten'
      ]
    }
  },
  hazardous_substances: {
    label: '4. Kontakt mit gesundheitsschädigenden Stoffen',
    description: 'A: Chemische und biologische Gefährdung. B: Gase, Dämpfe, Flüssigkeiten, Feststoffe, Mikroorganismen. C: Kontakt/Exposition.',
    details: {
      A: 'chemische und biologische Gefaehrdung',
      B: [
        'Gase / Daempfe',
        'Fluessigkeiten / Aerosole',
        'Feststoffe',
        'Mikroorganismen und Viren'
      ],
      C: [
        'Sich in Zonen aufhalten mit ...',
        'Versinken in ...',
        'Arbeiten ausfuehren mit ...',
        'Kontakt mit gesundheitsschaedigenden Stoffen',
        'Gases, Daempfen, Fluessigkeiten, Stauben ausgesetzt sein'
      ]
    }
  },
  fire_explosion: {
    label: '5. Brand- und Explosionsgefährdung',
    description: 'A: Brand-/Explosionsgefahr. B: Brennbare/explosive Stoffe, Explosivstoffe. C: Hantieren, Aufenthalt in Atmosphäre.',
    details: {
      A: 'Brand-/Explosionsgefahr',
      B: [
        'brennbare/explosionsfaehige Feststoffe, Fluessigkeiten, Gase (Zuendquelle = ausloesender Faktor)',
        'Explosivstoffe (Aktivierungsenergie = ausloesender Faktor)'
      ],
      C: [
        'Hantieren mit ...',
        'Aufenthalt in explosionsfaehiger Atmosphaere',
        'Manipulation mit Explosivstoff'
      ]
    }
  },
  thermal: {
    label: '6. Thermische Gefährdung',
    description: 'A: Thermische Gefährdung. B: Heisse/kalte Medien/Oberflächen, wechselnde Temperaturen. C: Arbeiten mit/in Nähe.',
    details: {
      A: 'Thermische Gefaehrdung',
      B: [
        'heisse oder kalte Medien, Oberflaechen oder Atmosphaere',
        'Schnell wechselnde Temperaturen'
      ],
      C: [
        'Arbeiten mit/in der Naehe von heissen/kalten Bereichen',
        'Aufenthalt in Klimazonen mit grossen Temperaturschwankungen'
      ]
    }
  },
  special_physical: {
    label: '7. Spezielle physikalische Einwirkung',
    description: 'A: Spezielle physikalische Einwirkung. B: Lärm, Knall, Ultraschall, Vibration, Strahlung, Felder, Druck. C: Aufenthalt/Arbeit in Zonen.',
    details: {
      A: 'Spezielle physikalische Einwirkung',
      B: [
        'Laermquelle (>85 dB(A))',
        'Knall',
        'Ultra-/Infraschallquelle',
        'vibrierende Arbeitsmittel',
        'nichtionisierende Strahlung (UV, Laser, Sonnenlicht)',
        'elektromagnetische Felder',
        'ionisierende Strahlung',
        'Unter-/Ueberdruck'
      ],
      C: [
        'Aufenthalt in Zonen mit Laerm',
        'Aufenthalt in Naehe moeglicher Knallereignisse',
        'Aufenthalt in Zonen mit Ultra-/Infraschall',
        'Arbeiten mit vibrierenden Arbeitsmitteln',
        'Aufenthalt bei Quellen nichtionisierender Strahlung',
        'Aufenthalt in Bereichen elektromagnetischer Felder',
        'Aufenthalt bei Quellen ionisierender Strahlung',
        'Arbeiten in Raeumen mit Unter-/Ueberdruck'
      ]
    }
  },
  environment: {
    label: '8. Belastung durch Arbeitsumgebungsbedingungen',
    description: 'A: Belastung durch Umgebungsbedingungen. B: Klima, Licht. C: Einwirkung, schlechte Beleuchtung.',
    details: {
      A: 'Belastung durch Umgebungsbedingungen',
      B: [
        'Hitze, Kaelte',
        'Zugluft, trockene/feuchte Luft',
        'Sonne, Nasse, Schnee',
        'Beleuchtungsverhaeltnisse'
      ],
      C: [
        'Unter Einwirkung von ...',
        'Arbeiten bei schlechter Beleuchtung'
      ]
    }
  },
  ergonomic: {
    label: '9. Vernachlässigung ergonomischer Grundsätze',
    description: 'A: Ergonomische Gefährdung. B: Repetitive Bewegung, Zwangshaltung, Lasten. C: Arbeiten in diesen Haltungen.',
    details: {
      A: 'ergonomische Gefaehrdung',
      B: [
        'repetitive Bewegung',
        'Zwangshaltung',
        'statische Haltearbeit',
        'schwere Gegenstaende / Lasten',
        'belastende Koerperhaltung'
      ],
      C: [
        'Arbeiten mit repetitiven Bewegungen',
        'In Zwangshaltung arbeiten',
        'mit statischer Haltearbeit arbeiten',
        'Heben und Tragen von Gewichten',
        'Ueber-Kopf-Arbeit',
        'in Knien, Buecken, Liegen arbeiten',
        'mit verdrehtem Kopf oder Oberkoerper arbeiten'
      ]
    }
  },
  psychosocial: {
    label: '10. Psychische Belastung',
    description: 'A: Psychische Belastung. B: Klima, Alleinarbeit, Kundenkontakt. C: Teamkultur, Mobbing, Überforderung.',
    details: {
      A: 'Psychische Belastung',
      B: [
        'schlechtes Arbeitsklima (Aerger, soziale Konflikte, Angst)',
        'allein arbeiten, Verantwortung, Zeitdruck',
        'Kontakt zu schwierigen Kunden, aggressiven Menschen'
      ],
      C: [
        'belastende Teamkultur',
        'Diskriminierung, Mobbing',
        'Ueberforderung, Unterforderung',
        'aggressives Verhalten, Drohung'
      ]
    }
  },
  unexpected: {
    label: '11. Unerwartete Aktion',
    description: 'A: Unerwartete Aktion. B: Interaktion Mensch/Schädigung, Risiko durch Qualifikation. C: Unkontrollierter Ablauf.',
    details: {
      A: 'Unerwartete Aktion',
      B: [
        'Interaktion des Menschen und Schaedigungen ist vorgegeben durch die energieeintragende Gefaehrdung',
        'Risiko wird durch Qualifikation/Instruktion/Verantwortung beeinflusst'
      ],
      C: [
        'unkontrollierter Ablauf, Temperaturanstieg, Absturz'
      ]
    }
  },
  energy_failure: {
    label: '12. Ausfall der Energieversorgung',
    description: 'A: Ausfall der Energieversorgung. B: Temperaturanstieg, Prozessabbruch. C: Absturz, ungeplante Prozesse.',
    details: {
      A: 'Ausfall der Energieversorgung',
      B: [
        'unkontrollierter Temperaturanstieg, Prozessabbruch'
      ],
      C: [
        'Absturz, ungeplante Prozesse'
      ]
    }
  },
  organization: {
    label: '13. Ungünstige oder unklare Organisation',
    description: 'A: Organisation. B: Ablauf, Schichtarbeit, Qualifikation. C: Schicht-/Nachtarbeit.',
    details: {
      A: 'Organisation',
      B: [
        'Arbeitsablauf',
        'Schicht-, Nachtarbeit',
        'Qualifikation',
        'Instruktion/Kompetenz',
        'Verantwortung'
      ],
      C: [
        'Arbeit in Schicht-/Nachtarbeit'
      ]
    }
  }
};

export class AIService {
  constructor() {
    console.log('🤖 [AI] AIService constructor called');
    this.apiKey = null;
    this.isInitialized = false;
    this.usageStats = {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalTokensUsed: 0,
      averageResponseTime: 0
    };
    this.defaultConfig = {
      model: 'gpt-4.1',
      temperature: 0.3,
      maxTokens: 4000
    };
    this.config = { ...this.defaultConfig };

    // Endpoints
    this.chatEndpoint = 'https://api.openai.com/v1/chat/completions';
    this.responsesEndpoint = 'https://api.openai.com/v1/responses';
  }

  /**
   * Initialize AI service with configuration
   */
  async init(config = {}) {
    this.config = { ...this.config, ...config };
    if (config.apiKey) {
      this.apiKey = config.apiKey.trim();
    }

    console.log('🔧 [AI] Configuration updated:', {
      model: this.config.model,
      temperature: this.config.temperature,
      maxTokens: this.config.maxTokens,
      hasApiKey: !!this.apiKey
    });

    if (!this.apiKey) {
      console.warn('⚠️ [AI] No API key configured - AI operations will fallback to templates');
    }

    this.isInitialized = true;
    return true;
  }

  isConfigured() {
    return !!this.apiKey;
  }

  /**
   * Map UI model names to real API model IDs
   * Unknown IDs werden NICHT auf 4.1 zurueckgesetzt, sondern direkt verwendet.
   */
  sanitizeModelId(model) {
    const fallback = this.defaultConfig.model;

    if (!model || typeof model !== 'string') return fallback;

    const raw = model.trim();
    const key = raw.toLowerCase();

    const MODEL_ID_MAPPING = {
      // GPT-5 Familie (Responses API)
      'gpt-5': 'gpt-5',
      'gpt-5.1': 'gpt-5.1',
      'gpt-5.1-instant': 'gpt-5.1',
      'gpt-5.1-thinking': 'gpt-5.1',
      'gpt-5-mini': 'gpt-5-mini',
      'gpt-5-nano': 'gpt-5-nano',

      // GPT-4.1 / 4o Familie (Chat)
      'gpt-4.1': 'gpt-4.1',
      'gpt-4.1-mini': 'gpt-4.1-mini',
      'gpt-4o': 'gpt-4o',
      'gpt-4o-mini': 'gpt-4o-mini',

      // o-Modelle (Reasoning, Chat)
      'o1': 'o1',
      'o1-mini': 'o1-mini',
      'o3-mini': 'o3-mini'
    };

    const mapped = MODEL_ID_MAPPING[key] || raw;
    return mapped || fallback;
  }

  /**
   * Determine model capabilities and parameter requirements
   */
  getModelCapabilities(modelId) {
    // Capability definitions for known models
    const MODEL_CAPABILITIES = {
      // ---------- GPT-4.1 Familie (Chat Completions) ----------
      'gpt-4.1': {
        family: 'standard',
        apiType: 'chat',
        tokenParam: 'max_tokens',
        supportsSystemMessages: true,
        supportsTemperature: true,
        supportsJson: true,
        supportsReasoningEffort: false
      },
      'gpt-4.1-mini': {
        family: 'standard',
        apiType: 'chat',
        tokenParam: 'max_tokens',
        supportsSystemMessages: true,
        supportsTemperature: true,
        supportsJson: true,
        supportsReasoningEffort: false
      },
      'gpt-4o': {
        family: 'standard',
        apiType: 'chat',
        tokenParam: 'max_tokens',
        supportsSystemMessages: true,
        supportsTemperature: true,
        supportsJson: true,
        supportsReasoningEffort: false
      },
      'gpt-4o-mini': {
        family: 'standard',
        apiType: 'chat',
        tokenParam: 'max_tokens',
        supportsSystemMessages: true,
        supportsTemperature: true,
        supportsJson: true,
        supportsReasoningEffort: false
      },

      // ---------- o-Serie (Reasoning, Chat Completions) ----------
      'o1': {
        family: 'reasoning',
        apiType: 'chat',
        tokenParam: 'max_completion_tokens',
        supportsSystemMessages: false,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },
      'o1-mini': {
        family: 'reasoning',
        apiType: 'chat',
        tokenParam: 'max_completion_tokens',
        supportsSystemMessages: false,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },
      'o3-mini': {
        family: 'reasoning',
        apiType: 'chat',
        tokenParam: 'max_completion_tokens',
        supportsSystemMessages: false,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },

      // ---------- GPT-5 Familie (Reasoning, Responses API) ----------
      'gpt-5': {
        family: 'reasoning',
        apiType: 'responses',
        tokenParam: 'max_output_tokens',
        supportsSystemMessages: true,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },
      'gpt-5.1': {
        family: 'reasoning',
        apiType: 'responses',
        tokenParam: 'max_output_tokens',
        supportsSystemMessages: true,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },
      'gpt-5-mini': {
        family: 'reasoning',
        apiType: 'responses',
        tokenParam: 'max_output_tokens',
        supportsSystemMessages: true,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      },
      'gpt-5-nano': {
        family: 'reasoning',
        apiType: 'responses',
        tokenParam: 'max_output_tokens',
        supportsSystemMessages: true,
        supportsTemperature: false,
        supportsJson: false,
        supportsReasoningEffort: true
      }
    };

    // Fallback: konservatives Standardprofil
    return MODEL_CAPABILITIES[modelId] || {
      family: 'standard',
      apiType: 'chat',
      tokenParam: 'max_tokens',
      supportsSystemMessages: true,
      supportsTemperature: true,
      supportsJson: true,
      supportsReasoningEffort: false
    };
  }

  /**
   * Interne Hilfe: Text aus Responses-API-Result extrahieren
   */
  extractTextFromResponses(json) {
    if (!json) return '';

    // Einfachster Weg: output_text (siehe GPT-5.1 Guide)
    if (typeof json.output_text === 'string') {
      return json.output_text;
    }

    // GPT-5 Responses API Format: output ist ein Array
    // output[0] kann "reasoning" sein, output[1] ist die "message"
    if (!Array.isArray(json.output) || json.output.length === 0) {
      return '';
    }

    // Suche nach dem ersten "message" type output
    const messageOutput = json.output.find(item => item.type === 'message');
    if (!messageOutput || !Array.isArray(messageOutput.content)) {
      return '';
    }

    const texts = [];
    for (const part of messageOutput.content) {
      if (!part) continue;

      // Direkt text property
      if (typeof part.text === 'string') {
        texts.push(part.text);
      }
      // output_text type mit text property
      else if (part.type === 'output_text' && typeof part.text === 'string') {
        texts.push(part.text);
      }
      // Nested output_text
      else if (part.output_text && typeof part.output_text.text === 'string') {
        texts.push(part.output_text.text);
      }
    }

    return texts.join('');
  }

  /**
   * Zentrale Nicht-Streaming Anfrage
   * - Standard & o-Modelle: Chat Completions
   * - GPT-5 Familie: Responses API
   *
   * Rueckgabe ist IMMER chat-aehnlich:
   *   { choices: [ { message: { role, content } } ] }
   */
  async callOpenAI(
    messages,
    { maxTokensMultiplier = 1, responseFormat = 'text' } = {}
  ) {
    if (!this.apiKey) {
      throw new Error('Kein OpenAI API-Schluessel konfiguriert');
    }

    const startTime = performance.now();
    const modelId = this.sanitizeModelId(this.config.model);
    const caps = this.getModelCapabilities(modelId);

    // Basis-Tokenlimit aus Config
    const baseMax = this.config.maxTokens * maxTokensMultiplier;

    // Reasoning-Modelle brauchen typischerweise mehr Output
    // Besonders bei Gefährdungsanalysen mit vielen Bereichen
    const effectiveMax =
      caps.family === 'reasoning'
        ? Math.max(baseMax, 8000) // Erhöht für umfangreiche Analysen
        : baseMax;

    let json;

    if (caps.apiType === 'responses') {
      // ---------- Responses API (GPT-5 Familie) ----------
      const body = {
        model: modelId,
        input: messages.map(m => ({
          role: m.role,
          content: m.content
        })),
        [caps.tokenParam]: Math.round(effectiveMax)
      };

      if (caps.supportsReasoningEffort) {
        body.reasoning = { effort: 'medium' }; // Default, spaeter ggf. konfigurierbar
      }

      console.log('🛰️ [AI] Responses request:', {
        model: body.model,
        tokenParam: caps.tokenParam,
        tokenLimit: body[caps.tokenParam],
        apiType: caps.apiType
      });

      const response = await fetch(this.responsesEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`
        },
        body: JSON.stringify(body)
      });

      const elapsed = performance.now() - startTime;

      if (!response.ok) {
        this.usageStats.failedRequests++;
        let errorDetail = `${response.status} ${response.statusText}`;
        try {
          const errorBody = await response.json();
          console.error('❌ [AI] Responses API error response:', errorBody);
          errorDetail = errorBody?.error?.message || errorDetail;
        } catch (error) {
          // ignore JSON parse errors
        }
        throw new Error(`OpenAI-Anfrage fehlgeschlagen: ${errorDetail}`);
      }

      json = await response.json();

      console.log('📦 [AI] Full Responses API JSON:', JSON.stringify(json, null, 2));

      // Check for incomplete response
      if (json.status === 'incomplete') {
        const reason = json.incomplete_details?.reason || 'unknown';
        console.warn(`⚠️ [AI] Response incomplete. Reason: ${reason}`);
        if (reason === 'max_output_tokens') {
          console.warn('💡 [AI] Consider increasing max_output_tokens for this request.');
        }
      }

      // Usage-Stats
      this.usageStats.totalRequests++;
      this.usageStats.successfulRequests++;
      this.usageStats.averageResponseTime =
        (this.usageStats.averageResponseTime * (this.usageStats.successfulRequests - 1) + elapsed) /
        this.usageStats.successfulRequests;

      const content = this.extractTextFromResponses(json) || '';
      console.log('📝 [AI] Extracted content from Responses API:', content ? content.substring(0, 200) + '...' : 'EMPTY');

      return {
        choices: [
          {
            message: {
              role: 'assistant',
              content
            }
          }
        ]
      };
    }

    // ---------- Chat Completions API (Standard + o-Serie) ----------

    let finalMessages = messages;

    // Handle System Messages: Merge if not supported
    if (!caps.supportsSystemMessages) {
      const systemMessage = messages.find(m => m.role === 'system');
      if (systemMessage) {
        finalMessages = messages.filter(m => m.role !== 'system');
        const firstUserMsgIndex = finalMessages.findIndex(m => m.role === 'user');
        if (firstUserMsgIndex !== -1) {
          finalMessages[firstUserMsgIndex] = {
            ...finalMessages[firstUserMsgIndex],
            content: `${systemMessage.content}\n\n${finalMessages[firstUserMsgIndex].content}`
          };
        } else {
          finalMessages.unshift({ role: 'user', content: systemMessage.content });
        }
      }
    }

    const body = {
      model: modelId,
      messages: finalMessages
    };

    // Handle Tokens
    body[caps.tokenParam] = Math.round(effectiveMax);

    // Handle Temperature
    if (caps.supportsTemperature) {
      body.temperature = this.config.temperature;
    }

    // Handle Response Format
    if (caps.supportsJson && responseFormat === 'json') {
      body.response_format = { type: 'json_object' };
    }

    console.log('🛰️ [AI] Chat request:', {
      model: body.model,
      tokenParam: caps.tokenParam,
      tokenLimit: body[caps.tokenParam],
      supportsSystemMessages: caps.supportsSystemMessages,
      supportsTemperature: caps.supportsTemperature,
      supportsJson: caps.supportsJson,
      apiType: caps.apiType
    });

    const response = await fetch(this.chatEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`
      },
      body: JSON.stringify(body)
    });

    const elapsed = performance.now() - startTime;

    if (!response.ok) {
      this.usageStats.failedRequests++;
      let errorDetail = `${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.json();
        console.error('❌ [AI] Chat API error response:', errorBody);
        errorDetail = errorBody?.error?.message || errorDetail;
      } catch (error) {
        // ignore JSON parse errors
      }
      throw new Error(`OpenAI-Anfrage fehlgeschlagen: ${errorDetail}`);
    }

    json = await response.json();

    this.usageStats.totalRequests++;
    this.usageStats.successfulRequests++;
    this.usageStats.averageResponseTime =
      (this.usageStats.averageResponseTime * (this.usageStats.successfulRequests - 1) + elapsed) /
      this.usageStats.successfulRequests;

    return json;
  }

  /**
   * Streaming:
   * - Chat-Modelle: echtes SSE-Streaming
   * - Responses-Modelle (GPT-5 Familie): Fallback auf non-stream callOpenAI
   */
  async callOpenAIStream(
    messages,
    { maxTokensMultiplier = 1, responseFormat = 'text', onToken } = {}
  ) {
    if (!this.apiKey) {
      throw new Error('Kein OpenAI API-Schluessel konfiguriert');
    }

    const modelId = this.sanitizeModelId(this.config.model);
    const caps = this.getModelCapabilities(modelId);

    // Responses API: aktuell kein eigener Streaming-Parser -> Fallback
    if (caps.apiType === 'responses') {
      console.warn(
        '⚠️ [AI] Streaming fuer GPT-5-Modelle via Responses API noch nicht implementiert, Fallback auf non-stream.'
      );
      const full = await this.callOpenAI(messages, { maxTokensMultiplier, responseFormat });
      const content = full?.choices?.[0]?.message?.content || '';
      if (onToken && content) {
        onToken(content, content);
      }
      return content;
    }

    const startTime = performance.now();

    // Chat Streaming Pfad
    let finalMessages = messages;

    // Handle System Messages: Merge if not supported
    if (!caps.supportsSystemMessages) {
      const systemMessage = messages.find(m => m.role === 'system');
      if (systemMessage) {
        finalMessages = messages.filter(m => m.role !== 'system');
        const firstUserMsgIndex = finalMessages.findIndex(m => m.role === 'user');
        if (firstUserMsgIndex !== -1) {
          finalMessages[firstUserMsgIndex] = {
            ...finalMessages[firstUserMsgIndex],
            content: `${systemMessage.content}\n\n${finalMessages[firstUserMsgIndex].content}`
          };
        } else {
          finalMessages.unshift({ role: 'user', content: systemMessage.content });
        }
      }
    }

    const baseMax = this.config.maxTokens * maxTokensMultiplier;
    const effectiveMax =
      caps.family === 'reasoning'
        ? Math.max(baseMax, 2000)
        : baseMax;

    const body = {
      model: modelId,
      messages: finalMessages,
      stream: true
    };

    body[caps.tokenParam] = Math.round(effectiveMax);

    if (caps.supportsTemperature) {
      body.temperature = this.config.temperature;
    }

    if (caps.supportsJson && responseFormat === 'json') {
      body.response_format = { type: 'json_object' };
    }

    const response = await fetch(this.chatEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.apiKey}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok || !response.body) {
      let errorDetail = `${response.status} ${response.statusText}`;
      try {
        const errorBody = await response.json();
        errorDetail = errorBody?.error?.message || errorDetail;
      } catch (error) {
        // ignore
      }
      throw new Error(`OpenAI-Streaming fehlgeschlagen: ${errorDetail}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let aggregatedText = '';

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const segments = buffer.split('\n\n');
      buffer = segments.pop() || '';

      for (const segment of segments) {
        const line = segment.trim();
        if (!line.startsWith('data:')) {
          continue;
        }

        const payload = line.slice(5).trim();
        if (!payload || payload === '[DONE]') {
          continue;
        }

        try {
          const parsed = JSON.parse(payload);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            aggregatedText += delta;
            if (onToken) {
              onToken(delta, aggregatedText);
            }
          }
        } catch (error) {
          console.warn('⚠️ [AI] Failed to parse streaming payload:', error.message);
        }
      }
    }

    buffer += decoder.decode(new Uint8Array(), { stream: false });
    if (buffer.trim().length > 0) {
      const trailingSegments = buffer.split('\n\n');
      trailingSegments.forEach(segment => {
        const line = segment.trim();
        if (!line.startsWith('data:')) return;
        const payload = line.slice(5).trim();
        if (!payload || payload === '[DONE]') return;
        try {
          const parsed = JSON.parse(payload);
          const delta = parsed.choices?.[0]?.delta?.content;
          if (delta) {
            aggregatedText += delta;
            if (onToken) {
              onToken(delta, aggregatedText);
            }
          }
        } catch (error) {
          console.warn('⚠️ [AI] Failed to parse trailing streaming payload:', error.message);
        }
      });
    }

    const elapsed = performance.now() - startTime;
    this.usageStats.totalRequests++;
    this.usageStats.successfulRequests++;
    this.usageStats.averageResponseTime =
      (this.usageStats.averageResponseTime * (this.usageStats.successfulRequests - 1) + elapsed) /
      this.usageStats.successfulRequests;

    return aggregatedText;
  }

  /**
   * Generate tree structure from business description
   */
  async generateTreeStructure(businessType, description = '', profile = 'standard') {
    const startTime = performance.now();
    this.usageStats.totalRequests++;

    console.log('🌳 [AI] Starting tree structure generation:', {
      businessType,
      descriptionLength: description.length,
      profile,
      hasApiKey: this.isConfigured()
    });

    try {
      if (!this.isConfigured()) {
        throw new Error('Kein OpenAI API-Schluessel konfiguriert');
      }

      const prompt = this.buildTreeGenerationPrompt(businessType, description, profile);
      console.log('🔧 [AI] Generated prompt for tree structure:', {
        promptLength: prompt.length,
        model: this.config.model,
        profile,
        promptContent: prompt
      });

      const response = await this.callOpenAI([
        {
          role: 'system',
          content:
            'Du bist ein Experte fuer Schweizer Betriebsarchitektur und erstellst ' +
            'ausschliesslich raeumliche, physische Hierarchien (Standorte → Gebaeude → Ebenen → Raeume → Zonen). ' +
            'Du darfst keine organisatorischen Einheiten, Rollen oder Teams aufnehmen.'
        },
        {
          role: 'user',
          content: prompt
        }
      ]);

      const content = response.choices?.[0]?.message?.content;
      console.log('📦 [AI] Raw response content:', content);
      const tree = this.parseTreeResponse(content);
      const generateTime = performance.now() - startTime;
      console.log('✅ [AI] Tree structure generated in', `${Math.round(generateTime)}ms`);
      return tree;
    } catch (error) {
      console.error('❌ Tree generation failed:', error);
      throw new Error('AI-Generierung fehlgeschlagen');
    }
  }

  /**
   * Analyze workplace hazards using SUVA methodology
   */
  async analyzeHazards(treeStructure, config = {}) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Kein OpenAI API-Schluessel konfiguriert');
      }

      console.log('🔍 [AI] Hazard Analysis Input:', {
        treeStructure: JSON.stringify(treeStructure, null, 2).substring(0, 500) + '...',
        config: {
          hazardsPerArea: config.hazardsPerArea,
          profile: config.profile,
          mode: config.mode,
          categoriesCount: config.categories?.length
        }
      });

      const prompt = this.buildHazardAnalysisPrompt(treeStructure, config);

      let content = '';

      try {
        content = await this.callOpenAIStream(
          [
            {
              role: 'system',
              content:
                'Du bist ein SUVA-zertifizierter Sicherheitsexperte ' +
                'und analysierst Arbeitsplaetze nach den 13 SUVA Gefaehrdungskategorien.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          {
            maxTokensMultiplier: 4,
            onToken: config.onToken
          }
        );
      } catch (streamError) {
        console.warn(
          '⚠️ [AI] Streaming fehlgeschlagen, fallback auf Standardmodus:',
          streamError.message
        );
        this.usageStats.failedRequests++;
        const response = await this.callOpenAI(
          [
            {
              role: 'system',
              content:
                'Du bist ein SUVA-zertifizierter Sicherheitsexperte ' +
                'und analysierst Arbeitsplaetze nach den 13 SUVA Gefaehrdungskategorien.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          { maxTokensMultiplier: 4 }
        );
        content = response.choices?.[0]?.message?.content;
      }

      return this.parseHazardResponse(content);
    } catch (error) {
      console.error('❌ Hazard analysis failed:', error);
      throw new Error('Gefaehrdungsanalyse fehlgeschlagen');
    }
  }

  /**
   * Generate a single additional hazard for a specific area
   */
  async generateSingleHazard(area, existingHazards, config = {}, profileData = {}) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Kein OpenAI API-Schluessel konfiguriert');
      }

      const mode = config.mode || 'standard';
      const category = config.category; // Spezifische Kategorie, falls gewünscht

      const modeInstructions =
        mode === 'chaos'
          ? 'ACHTUNG: Dies ist ein "Chaos-Betrieb". Gehe von schlechter Wartung, fehlenden Sicherheitsvorkehrungen, ungeschultem Personal und improvisierten Loesungen aus.'
          : 'Dies ist ein serioes arbeitender Betrieb.';

      const existingDescriptions = existingHazards
        .filter(h => h.area === area)
        .map(h => `- ${h.description}`)
        .join('\n');

      // Kategorie-spezifische Anweisung
      const categoryInstruction = category
        ? `\nWICHTIG: Die Gefaehrdung MUSS aus der Kategorie "${HAZARD_CATEGORY_METADATA[category]?.label || category}" stammen.`
        : '';

      // Special Risk Logic
      const isSpecialArea = area.includes('Spezielle Gefährdungen') || area.includes('Betriebsmittel');
      let specialRiskContext = '';

      const hasConditions = profileData.conditions && Object.values(profileData.conditions).some(v => v);

      if (isSpecialArea && (profileData.machines || profileData.chemicals || profileData.description || hasConditions)) {

        const conditions = [];
        if (profileData.conditions?.shiftwork) conditions.push('- Schichtarbeit / Nachtarbeit');
        if (profileData.conditions?.mobile) conditions.push('- Mobile Arbeitsplätze / Aussendienst');
        if (profileData.conditions?.pregnant) conditions.push('- Schwangere / Stillende Frauen');
        if (profileData.conditions?.young) conditions.push('- Jugendliche / Lernende');
        if (profileData.conditions?.alone) conditions.push('- Alleinarbeit');

        specialRiskContext = `
WICHTIG - SPEZIAL-GEFÄHRDUNG:
Dieser Bereich "${area}" sammelt spezifische Risiken basierend auf den Betriebsmitteln und Arbeitsbedingungen.
Nutze folgende Informationen, um eine KONKRETE Gefährdung zu generieren:
- Maschinen: ${profileData.machines || 'Keine'}
- Chemikalien: ${profileData.chemicals || 'Keine'}
- Beschreibung: ${profileData.description || 'Keine'}
- Spezielle Arbeitsbedingungen:
${conditions.length > 0 ? conditions.join('\n') : 'Keine'}

Erstelle eine Gefährdung, die DIREKT mit einer dieser Angaben zusammenhängt (z.B. Quetschgefahr an der Presse, Verätzung durch Säure, erhöhtes Unfallrisiko bei Alleinarbeit).
Ignoriere allgemeine Raum-Gefahren.
`;
      }

      const prompt = `
Analysiere den Bereich "${area}" und identifiziere GENAU EINE (1) WEITERE, NEUE Gefaehrdung.${categoryInstruction}

BETRIEBSMODUS: ${modeInstructions}
${specialRiskContext}

Bereits identifizierte Gefaehrdungen (NICHT DUPLIZIEREN):
${existingDescriptions}

WICHTIG - GEFAHRENPOTENZIAL (RISIKO) EINSCHAETZUNG:
- Gefahrenpotenzial hoch: Ein hohes Gefahrenpotenzial bedeutet, dass eine besondere Gefahr vorliegt oder ein bleibender (irreversibler) Gesundheitsschaden moeglich ist.
- Gefahrenpotenzial tief: Ein tiefes Gefahrenpotenzial bedeutet, dass eine Beeintraechtigung der Gesundheit moeglich ist, aber der zu erwartende Gesundheitsschaden immer heilbar (reversibel) ist.

WICHTIG - ANERKANNTE REGELN:
Bewerte, ob anerkannte Regeln (Gesetze, Verordnungen, Normen, SUVA-Richtlinien) verfuegbar sind.

Gib das Ergebnis als JSON zurueck (ein einzelnes Objekt, kein Array):
{
  "area": "${area}",
  "category": "${category ? (HAZARD_CATEGORY_METADATA[category]?.label || category) : 'Kategorie'}",
  "subcategory": "Spezifische Gefaehrdung",
  "description": "Detaillierte Beschreibung",
  "realisticInjuries": "Moegliche Verletzungen",
  "riskLevel": "hoch|tief",
  "rulesAvailable": true|false,
  "suvaGuidelines": ["Referenz 1"],
  "remediation": {
    "priority": "sofort|fristig|mittel|langfristig",
    "measures": ["Massnahme 1"],
    "costEstimate": "CHF ...",
    "stopLevel": "S|T|O|P"
  }
}
      `;

      const response = await this.callOpenAI([
        {
          role: 'system',
          content: 'Du bist ein SUVA-zertifizierter Sicherheitsexperte.'
        },
        {
          role: 'user',
          content: prompt
        }
      ]);

      const content = response.choices?.[0]?.message?.content;
      const jsonString = this.extractJson(content);
      if (!jsonString) throw new Error('No JSON found');

      return JSON.parse(jsonString);
    } catch (error) {
      console.error('❌ Single hazard generation failed:', error);
      throw error;
    }
  }


  /**
   * Generate a specific hazard based on a source value (e.g. machine name)
   */
  async generateSpecificHazard(sourceType, sourceValue, existingHazards) {
    try {
      if (!this.isConfigured()) {
        throw new Error('Kein OpenAI API-Schluessel konfiguriert');
      }

      console.log(`🎯 [AI] Generating specific hazard for ${sourceType}: "${sourceValue}"`);

      const existingDescriptions = existingHazards
        .filter(h => h.area.includes('Spezielle Gefährdungen') || h.area.includes('Betriebsmittel'))
        .map(h => `- ${h.description}`)
        .join('\n');

      const prompt = `
Analysiere den spezifischen Aspekt "${sourceValue}" (Typ: ${sourceType}) eines Betriebs und identifiziere GENAU EINE (1) KONKRETE Gefaehrdung.

KONTEXT:
Dies ist eine gezielte Analyse für den Bereich "Spezielle Gefährdungen & Betriebsmittel".
Der Benutzer möchte explizit eine Gefahr hinzufügen, die von "${sourceValue}" ausgeht.

Bereits identifizierte Gefaehrdungen in diesem Bereich (NICHT DUPLIZIEREN):
${existingDescriptions}

ANWEISUNGEN:
1. Identifiziere eine realistische, signifikante Gefährdung, die direkt durch "${sourceValue}" verursacht wird.
2. Wähle die passendste SUVA-Kategorie (z.B. "Mechanische Gefährdungen" für Maschinen, "Stoffe" für Chemikalien).
3. Beschreibe die Gefahr präzise.

Gib das Ergebnis als JSON zurueck (ein einzelnes Objekt):
{
  "area": "Spezielle Gefährdungen & Betriebsmittel",
  "category": "Passende SUVA Kategorie",
  "subcategory": "${sourceValue}",
  "description": "Detaillierte Beschreibung der Gefahr durch ${sourceValue}",
  "realisticInjuries": "Moegliche Verletzungen",
  "riskLevel": "hoch|tief",
  "rulesAvailable": true|false,
  "suvaGuidelines": ["Relevante Richtlinie"],
  "remediation": {
    "priority": "sofort|fristig|mittel",
    "measures": ["Konkrete Massnahme"],
    "costEstimate": "CHF ...",
    "stopLevel": "S|T|O|P"
  }
}
      `;

      const response = await this.callOpenAI([
        {
          role: 'system',
          content: 'Du bist ein SUVA-zertifizierter Sicherheitsexperte.'
        },
        {
          role: 'user',
          content: prompt
        }
      ]);

      const content = response.choices?.[0]?.message?.content;
      const jsonString = this.extractJson(content);
      if (!jsonString) throw new Error('No JSON found');

      return JSON.parse(jsonString);
    } catch (error) {
      console.error('❌ Specific hazard generation failed:', error);
      throw error;
    }
  }

  /**
   * Build prompt for tree generation
   */
  buildTreeGenerationPrompt(businessType, description, profile = 'standard') {
    const baseDescription = this.getBusinessTypeDescription(businessType);
    const locationName = description || baseDescription;

    const scopeInstruction = profile === 'comprehensive'
      ? '- Nutze ausschliesslich physische Bereiche wie Gebaeude, Trakte, Etagen,\n  Raeume, Lagerzonen und Aussenbereiche.'
      : '- Nutze ausschliesslich physische Innenbereiche wie Gebaeude, Trakte, Etagen,\n  Raeume und Lagerzonen. Keine Aussenbereiche.';

    return `
Erstelle eine strikt raeumliche Struktur fuer den Betrieb "${locationName}" (${baseDescription}).

WICHTIG:
${scopeInstruction}
- Keine organisatorischen Abteilungen, Rollen, Teams oder Prozesse nennen.
- Beginne mit dem Gesamtstandort und arbeite dich hierarchisch nach unten
  (Standort → Gebaeude → Etage → Raum/Zonen).
- Verwende klare raeumliche Bezeichnungen.
- Beruecksichtige typische Nebenflaechen: Technikraeume, Sanitaeranlagen,
  Verkehrsflächen, Notausgaenge.

Gib die Antwort als JSON-Hierarchie im folgenden Format zurueck:
{
  "name": "${locationName}",
  "type": "site",
  "children": [
    {
      "name": "Gebaeude A",
      "type": "building",
      "children": [
        {
          "name": "Erdgeschoss",
          "type": "level",
          "children": [
            { "name": "Empfang", "type": "space" }
          ]
        }
      ]
    }
  ]
}

Mindestens 3 Ebenen (Standort → Gebaeude → Etage → Raum) sind erforderlich.
Bei Bedarf kann eine vierte Ebene (Zonen/Stationen) ergaenzt werden.
    `;
  }

  /**
   * Build prompt for hazard analysis
   */
  buildHazardAnalysisPrompt(treeStructure, config) {
    const hazardsPerArea = Math.max(parseInt(config.hazardsPerArea, 10) || 3, 1);
    const assessmentProfile = config.profile || 'standard';
    const mode = config.mode || 'standard';
    const selectedCategories =
      Array.isArray(config.categories) && config.categories.length > 0
        ? config.categories
        : Object.keys(HAZARD_CATEGORY_METADATA);

    const categoryText = selectedCategories
      .map((key, index) => {
        const meta = HAZARD_CATEGORY_METADATA[key] || { label: key, description: '' };
        return `${index + 1}. ${meta.label}${meta.description ? ` – ${meta.description}` : ''}`;
      })
      .join('\n');

    const profileInstructions =
      assessmentProfile === 'comprehensive'
        ? 'Beruecksichtige Innen- und Aussenbereiche inklusive Verkehrswege, Lagerflaechen im Freien und Wettereinfluesse.'
        : 'Konzentriere dich primaer auf Innenbereiche und unmittelbar angrenzende Zonen.';

    const modeInstructions =
      mode === 'chaos'
        .join('\n\n');

    // Construct special risk context if profile data is available
    let specialRiskContext = '';
    if (profileData) {
      const conditions = [];
      if (profileData.conditions?.shiftwork) conditions.push('Schichtarbeit/Nachtarbeit');
      if (profileData.conditions?.mobile) conditions.push('Mobile Arbeit/Aussendienst');
      if (profileData.conditions?.pregnant) conditions.push('Schwangere/Stillende');
      if (profileData.conditions?.young) conditions.push('Jugendliche/Lernende');
      if (profileData.conditions?.alone) conditions.push('Alleinarbeit');

      specialRiskContext = `
ZUSAETZLICHER KONTEXT (Erweitertes Firmenprofil):
- Maschinen/Anlagen: ${profileData.machines || 'Keine spezifischen Angaben'}
- Chemikalien/Stoffe: ${profileData.chemicals || 'Keine spezifischen Angaben'}
- Spezielle Beschreibung: ${profileData.description || 'Keine'}
- Spezielle Bedingungen: ${conditions.length > 0 ? conditions.join(', ') : 'Keine'}
      `;
    }

    return `
Analysiere die folgende Betriebsstruktur auf Sicherheitsgefaehrdungen nach SUVA-Methodik.

BETRIEBSSTRUKTUR:
${JSON.stringify(treeStructure, null, 2)}

- Gefahrenpotenzial hoch: Ein hohes Gefahrenpotenzial bedeutet, dass eine besondere Gefahr vorliegt oder ein bleibender (irreversibler) Gesundheitsschaden moeglich ist.
- Gefahrenpotenzial tief: Ein tiefes Gefahrenpotenzial bedeutet, dass eine Beeintraechtigung der Gesundheit moeglich ist, aber der zu erwartende Gesundheitsschaden immer heilbar (reversibel) ist.

WICHTIG - ANERKANNTE REGELN:
Bewerte fuer jede Gefaehrdung, ob anerkannte Regeln (Gesetze, Verordnungen, Normen, SUVA-Richtlinien) verfuegbar sind.
- true: Regeln sind verfuegbar und bekannt.
- false: Regeln sind nicht oder nur teilweise verfuegbar/bekannt.

Gib deine Analyse als JSON zurueck:
{
  "hazards": [
    {
      "id": "hazard_001",
      "area": "Bereichsname",
      "category": "Mechanische Gefaehrdungen",
      "subcategory": "Spezifische Gefaehrdung",
      "description": "Detaillierte Beschreibung",
      "realisticInjuries": "Beschreibung der realistischen Verletzungen (z.B. 'Schnittwunde', 'Knochenbruch', 'Verlust von Gliedmassen')",
      "riskLevel": "hoch|tief",
      "rulesAvailable": true,
      "suvaGuidelines": ["Referenz 1", "Referenz 2"],
      "remediation": {
        "priority": "sofort|fristig|mittel|langfristig",
        "measures": ["Massnahme 1", "Massnahme 2"],
        "costEstimate": "CHF 0-1000|1000-5000|5000+",
        "stopLevel": "S|T|O|P"
      }
    }
  ]
}

Nur JSON zurueckgeben, ohne erlaeuternden Fliesstext ausserhalb der JSON-Struktur.
    `;
  }

  /**
   * Helper to extract JSON from potential Markdown or text
   */
  extractJson(content) {
    if (!content) return null;

    // 1. Try to find Markdown JSON block
    const markdownMatch = content.match(/```json\s*([\s\S]*?)\s*```/);
    if (markdownMatch && markdownMatch[1]) {
      return markdownMatch[1];
    }

    // 2. Try to find Markdown block without language
    const codeBlockMatch = content.match(/```\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch && codeBlockMatch[1]) {
      if (codeBlockMatch[1].trim().startsWith('{')) {
        return codeBlockMatch[1];
      }
    }

    // 3. Fallback: Find first '{' and last '}'
    const firstBrace = content.indexOf('{');
    const lastBrace = content.lastIndexOf('}');

    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      return content.substring(firstBrace, lastBrace + 1);
    }

    return null;
  }

  /**
   * Parse AI tree response
   */
  parseTreeResponse(content) {
    try {
      if (!content) {
        throw new Error('Content is empty or undefined');
      }

      const jsonString = this.extractJson(content);
      if (!jsonString) {
        throw new Error('No JSON structure found in response');
      }

      return JSON.parse(jsonString);
    } catch (error) {
      console.error(
        '❌ Failed to parse tree response. Content preview:',
        content ? content.substring(0, 200) + '...' : 'null'
      );
      console.error('Parse error:', error);
      throw new Error('Ungueltige KI-Antwort: JSON konnte nicht extrahiert werden.');
    }
  }

  /**
   * Parse AI hazard response
   */
  parseHazardResponse(content) {
    try {
      if (!content) {
        throw new Error('Content is empty or undefined');
      }

      let jsonString = this.extractJson(content);

      // Fallback for truncated content where extractJson might fail or return incomplete JSON
      if (!jsonString) {
        const firstBrace = content.indexOf('{');
        if (firstBrace !== -1) {
          jsonString = content.substring(firstBrace);
        } else {
          throw new Error('No JSON structure found in response');
        }
      }

      try {
        return JSON.parse(jsonString);
      } catch (parseError) {
        console.warn('⚠️ [AI] Standard JSON parsing failed, attempting repair:', parseError.message);
        try {
          const repaired = this.repairJson(jsonString);
          return JSON.parse(repaired);
        } catch (repairError) {
          throw new Error(`JSON repair failed: ${repairError.message}`);
        }
      }
    } catch (error) {
      console.error(
        '❌ Failed to parse hazard response. Content preview:',
        content ? content.substring(0, 200) + '...' : 'null'
      );
      console.error('Parse error:', error);
      throw new Error('Ungueltige Gefaehrdungsanalyse: JSON konnte nicht extrahiert werden.');
    }
  }

  /**
   * Attempt to repair truncated JSON
   */
  repairJson(jsonString) {
    let fixed = jsonString.trim();

    // Remove trailing comma if present (ignoring whitespace)
    fixed = fixed.replace(/,\s*$/, '');

    // Count brackets to determine what's missing
    // Note: This is a simple heuristic and won't handle braces inside strings correctly
    // but is often sufficient for LLM output repair
    let openBraces = (fixed.match(/{/g) || []).length;
    let closeBraces = (fixed.match(/}/g) || []).length;
    let openBrackets = (fixed.match(/\[/g) || []).length;
    let closeBrackets = (fixed.match(/]/g) || []).length;

    // Close arrays first (usually inner)
    while (openBrackets > closeBrackets) {
      fixed += ']';
      closeBrackets++;
    }

    // Close objects
    while (openBraces > closeBraces) {
      fixed += '}';
      closeBraces++;
    }

    return fixed;
  }

  /**
   * Get business type description in German
   */
  getBusinessTypeDescription(type) {
    const descriptions = {
      restaurant: 'Restaurant',
      retail: 'Einzelhandelsgeschaeft',
      manufacturing: 'Produktionsbetrieb',
      office: 'Buerounternehmen',
      custom: 'benutzerdefiniertes Unternehmen'
    };

    return descriptions[type] || 'Unternehmen';
  }

  /**
   * Validate API key
   */
  async validateAPIKey() {
    return this.isConfigured();
  }

  /**
   * Get available models
   */
  async getAvailableModels() {
    return [];
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return { ...this.config };
  }
}
